import { formatDate, formatTimeWithZoneAbbreviation } from ".";
import { DATE_FORMAT } from "../constants/app-constant";

export const SIGNATURE_FONT_FAMILY = '"Caveat", "Dancing Script", cursive';

export const renderSignatureCanvas = async (
  text: string,
  fontSizeRem: number
): Promise<HTMLCanvasElement> => {
  const scale = window.devicePixelRatio || 2;
  const fontSizePx = fontSizeRem * 16;
  const font = `500 ${fontSizePx}px ${SIGNATURE_FONT_FAMILY}`;

  await document.fonts.load(font);

  const measureCanvas = document.createElement("canvas");
  const measureCtx = measureCanvas.getContext("2d");
  if (!measureCtx) {
    throw new Error("Could not measure signature text");
  }

  measureCtx.font = font;
  const metrics = measureCtx.measureText(text);
  const padding = { top: 2, right: 10, bottom: 6, left: 10 };

  const ascent =
    metrics.fontBoundingBoxAscent ??
    metrics.actualBoundingBoxAscent ??
    fontSizePx * 0.85;
  const descent =
    metrics.fontBoundingBoxDescent ??
    metrics.actualBoundingBoxDescent ??
    fontSizePx * 0.35;

  const width = Math.ceil(metrics.width + padding.left + padding.right);
  const height = Math.ceil(ascent + descent + padding.top + padding.bottom);

  const canvas = document.createElement("canvas");
  canvas.width = width * scale;
  canvas.height = height * scale;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Could not render signature");
  }

  ctx.scale(scale, scale);
  ctx.font = font;
  ctx.fillStyle = "#000000";
  ctx.textBaseline = "alphabetic";
  ctx.fillText(text, padding.left, padding.top + ascent);

  return canvas;
};

export interface SignatureDateDisplay {
  show: boolean;
  label: string;
  text: string;
}

export const getSignatureDateDisplay = ({
  signatureInfo,
  hideSubmit,
  isPreviewMode,
  timezoneName,
  timezoneAbbrev,
}: {
  signatureInfo?: { UPDATED_DATE?: string };
  hideSubmit: boolean;
  isPreviewMode: boolean;
  timezoneName?: string;
  timezoneAbbrev?: string;
}): SignatureDateDisplay => {
  const showSubmittedDate = Boolean(signatureInfo?.UPDATED_DATE && hideSubmit);
  const showPreviewSavedDate = Boolean(
    isPreviewMode && signatureInfo?.UPDATED_DATE && !showSubmittedDate
  );

  if (!showSubmittedDate && !showPreviewSavedDate) {
    return { show: false, label: "", text: "" };
  }

  const updatedDate = signatureInfo?.UPDATED_DATE ?? "";
  const label = showSubmittedDate ? "Query Submitted on:" : "Saved on:";

  return {
    show: true,
    label,
    text: `${formatDate(
      updatedDate,
      DATE_FORMAT.MM_DD_YYYY,
      timezoneName
    )} at ${formatTimeWithZoneAbbreviation(
      updatedDate,
      timezoneName,
      timezoneAbbrev
    )}`,
  };
};

export const getSignatureSignerName = (
  providerName?: string,
  signatureInfo?: { FIRST_NAME?: string; LAST_NAME?: string }
): string => {
  if (providerName?.trim()) return providerName.trim();

  if (signatureInfo?.FIRST_NAME || signatureInfo?.LAST_NAME) {
    return `${signatureInfo.FIRST_NAME ?? ""} ${signatureInfo.LAST_NAME ?? ""}`.trim();
  }

  return "";
};
